import { useMemo, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'

import { getInspectorPositionForOrder } from '@/api/inspector_eta'
import { listSystemConfig } from '@/api/system_config'
import { useInspectorEtaRealtime } from '@/hooks/useInspectorEtaRealtime'
import { haversineKm } from '@/features/dispatch-map/inspectorIcon'
import { dateFnsLocale } from '@/utils/dateLocale'

interface Props {
  orderId: string
  inspectorId: string | null
  /** Координаты заказа из orders.lat/lon. Если null — расстояние посчитать нельзя. */
  orderLat: number | null | undefined
  orderLon: number | null | undefined
}

/**
 * «Текущий момент» через useSyncExternalStore — даёт чистое значение для
 * рендера и сам обновляется по интервалу. Заменяет прямой `Date.now()`
 * в теле компонента, на который ругается react-compiler.
 */
function useNow(intervalMs = 30_000): number {
  return useSyncExternalStore<number>(
    (cb) => {
      const t = setInterval(cb, intervalMs)
      return () => clearInterval(t)
    },
    () => Date.now(),
    () => Date.now(),
  )
}

/**
 * Блок «Расстояние / ETA» в карточке заказа.
 *
 * Виден только если у заказа назначен инспектор (`inspectorId != null`).
 * Тянет позицию инспектора через view `inspector_position_for_dealer`
 * (RLS пускает admin/dispatcher и manager-создателя). Если view вернула
 * NULL — показываем «Местоположение инспектора недоступно».
 *
 * Расстояние — гаверсинус по координатам заказа и инспектора (на клиенте).
 * ETA — `(km / system_config.eta_avg_speed_kmh) * 60` минут.
 * Realtime — подписка на UPDATE `profiles WHERE id=eq.{inspectorId}`.
 */
export function OrderInspectorEtaBlock({
  orderId,
  inspectorId,
  orderLat,
  orderLon,
}: Props) {
  const { t } = useTranslation()
  useInspectorEtaRealtime(orderId, inspectorId)
  // Текущий момент — для возраста позиции; обновляется раз в 30 сек.
  const now = useNow()

  const { data: cfg } = useQuery({
    queryKey: ['system-config'],
    queryFn: listSystemConfig,
    staleTime: 60_000,
  })
  const freshMinutes = useMemo(() => {
    const v = cfg?.find((c) => c.key === 'position_fresh_minutes')?.value
    const n = v ? Number(v) : 5
    return Number.isFinite(n) && n > 0 ? n : 5
  }, [cfg])
  const avgSpeed = useMemo(() => {
    const v = cfg?.find((c) => c.key === 'eta_avg_speed_kmh')?.value
    const n = v ? Number(v) : 40
    return Number.isFinite(n) && n > 0 ? n : 40
  }, [cfg])

  const { data: pos, isLoading } = useQuery({
    queryKey: ['inspector-eta', orderId],
    queryFn: () => getInspectorPositionForOrder(orderId),
    enabled: Boolean(inspectorId),
  })

  if (!inspectorId) return null
  if (isLoading) {
    return <Wrap><Muted>{t('orderBlocks.eta.loadingPosition')}</Muted></Wrap>
  }

  // Позиция вообще отсутствует (нет данных в profiles или RLS не пустила).
  if (!pos || pos.last_lat == null || pos.last_lon == null || !pos.last_position_at) {
    return (
      <Wrap>
        <Muted>{t('orderBlocks.eta.positionUnavailable')}</Muted>
      </Wrap>
    )
  }
  if (orderLat == null || orderLon == null) {
    return (
      <Wrap>
        <Muted>{t('orderBlocks.eta.noOrderCoords')}</Muted>
      </Wrap>
    )
  }

  const km = haversineKm(
    { lat: orderLat, lon: orderLon },
    { lat: pos.last_lat, lon: pos.last_lon },
  )
  const etaMin = Math.max(1, Math.round((km / avgSpeed) * 60))
  const ageMin = (now - new Date(pos.last_position_at).getTime()) / 60_000
  const isStale = ageMin > freshMinutes
  const ageText = formatDistanceToNow(new Date(pos.last_position_at), {
    locale: dateFnsLocale(),
    addSuffix: true,
  })

  return (
    <Wrap>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-[color:var(--stryzh-color-text-secondary)]">{t('orderBlocks.eta.distance')}</span>
        <span className="font-mono text-[color:var(--stryzh-color-text-primary)]">
          {t('orderBlocks.eta.km', { value: km.toFixed(1) })}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-[color:var(--stryzh-color-text-secondary)]">
          {t('orderBlocks.eta.etaLabel')}{' '}
          <span
            title={t('orderBlocks.eta.tooltip', { speed: avgSpeed })}
            className="cursor-help opacity-60"
          >
            ⓘ
          </span>
          :
        </span>
        <span className="font-mono text-[color:var(--stryzh-color-text-primary)]">
          {t('orderBlocks.eta.min', { value: etaMin })}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-[color:var(--stryzh-color-text-secondary)]">{t('orderBlocks.eta.updated')}</span>
        <span
          className={
            isStale
              ? 'text-yellow-300'
              : 'text-[color:var(--stryzh-color-text-primary)]'
          }
          title={isStale ? t('orderBlocks.eta.staleTitle', { minutes: freshMinutes }) : undefined}
        >
          {ageText}
          {isStale ? ' ⚠️' : ''}
        </span>
      </div>
    </Wrap>
  )
}

function Wrap({ children }: { children: React.ReactNode }) {
  // Заголовок и обвязка — на стороне родительского `<section className={card}>`
  // в OrderDetailPage. Сам блок просто отдаёт content.
  return <div className="space-y-1">{children}</div>
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
      {children}
    </div>
  )
}
